/**
 * 画面間の遷移。
 *
 * 「経路 → その画面を開ける最低ロール」の表をここ 1 箇所に置き、
 * ルーターのガード (`main.tsx`)・サイドバー・画面間のリンクの 3 者が
 * 同じ表を見る。判定は `@inou/shared` の `roleAtLeast` (API のガードと同じ)。
 * ここが割れると「サイドバーには出ないのにリンクからは入れる」がすぐ生まれる。
 *
 * 画面の状態 (対象月・絞り込み・開いている行) は URL のクエリが持つ。
 * 飛び先を「特定のエンティティを選んだ状態」で開くのに専用のルートを
 * 増やさずに済み、リロードや共有でも同じ状態に戻る。
 *   ?month=YYYY-MM  MonthPicker のある画面の対象月
 *   ?q=<文字列>     一覧の絞り込み (SearchInput の値。照合は matchesQuery)
 *   ?project=<id>   プロジェクト一覧で開いている行
 *   ?user=<id>      メンバー一覧で開いている行
 *
 * ただし**初期表示で URL を書き換えない**。当月や空の絞り込みを毎回
 * クエリに焼くと、ただ画面を開いただけで URL が変わる。
 */
import { roleAtLeast, type UserRole } from '@inou/shared';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

import { currentMonth } from './format';

export interface AppRoute {
  path: string;
  /** サイドバーとリンクで共通の表示名 */
  label: string;
  /** この画面を開ける最低ロール */
  minRole: UserRole;
}

/**
 * ログイン後の画面。`main.tsx` の `<Route>` もこの表から引く。
 *
 * 個人に紐付く画面 (アカウント・MCP接続) は全ロールが開ける。
 * テナントのものは Full Management プラン (member 以上)、
 * マスタ管理は admin。工数明細だけは mcp にも開ける
 * (自分の記録を直す口が無いと、MCP から入れた数字を誰も直せない)。
 *
 * 「はじめに」は開通直後の案内。**mcp には出さない** ので minRole は member。
 * mcp の home は MCP接続の画面そのもので、案内としてはそれで足りている。
 */
export const ROUTES = {
  start: { path: '/start', label: 'はじめに', minRole: 'member' },
  utilization: { path: '/utilization', label: 'アサイン・稼働', minRole: 'member' },
  capitalization: { path: '/capitalization', label: '収支・資産化', minRole: 'member' },
  assignments: { path: '/assignments', label: 'アサイン計画', minRole: 'member' },
  timeLogs: { path: '/time-logs', label: '工数明細', minRole: 'mcp' },
  projects: { path: '/projects', label: 'プロジェクト', minRole: 'member' },
  members: { path: '/members', label: 'メンバー', minRole: 'admin' },
  tenantSettings: { path: '/settings/tenant', label: 'テナント設定', minRole: 'member' },
  auditLogs: { path: '/audit-logs', label: '監査ログ', minRole: 'admin' },
  mcpSettings: { path: '/settings/mcp', label: 'MCP接続', minRole: 'mcp' },
  account: { path: '/settings/account', label: 'アカウント', minRole: 'mcp' },
} as const satisfies Record<string, AppRoute>;

/** リンクの飛び先。開ける人にだけ見せるので、経路と一緒に最低ロールを運ぶ。 */
export interface NavTarget {
  to: string;
  label: string;
  minRole: UserRole;
}

/** その人がこの飛び先を開けるか。 */
export function canOpen(role: UserRole, target: { minRole: UserRole }): boolean {
  return roleAtLeast(role, target.minRole);
}

function withParams(route: AppRoute, params: Record<string, string | undefined>): NavTarget {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, value);
  }
  const query = search.toString();
  return {
    to: query === '' ? route.path : `${route.path}?${query}`,
    label: route.label,
    minRole: route.minRole,
  };
}

/** プロジェクト一覧を、その行を開いた状態で。名前でも絞って 1 件に寄せる。 */
export function toProject(project: { id: string; name: string }): NavTarget {
  return withParams(ROUTES.projects, { project: project.id, q: project.name });
}

/** プロジェクト一覧の絞り込み (種別名・リポジトリ・プロジェクト名のいずれか)。 */
export function toProjects(query: string): NavTarget {
  return withParams(ROUTES.projects, { q: query });
}

/** メンバー一覧を、その行を開いた状態で。 */
export function toMember(member: { id: string; name: string }): NavTarget {
  return withParams(ROUTES.members, { user: member.id, q: member.name });
}

/**
 * メンバー一覧。行を開かずに一覧そのものへ。
 * 個人の単価を直しに行く先であり、招待もこの画面から。
 */
export function toMembers(): NavTarget {
  return withParams(ROUTES.members, {});
}

export function toAssignments(options: { month?: string; query?: string } = {}): NavTarget {
  return withParams(ROUTES.assignments, { month: options.month, q: options.query });
}

export function toTimeLogs(options: { month?: string; query?: string } = {}): NavTarget {
  return withParams(ROUTES.timeLogs, { month: options.month, q: options.query });
}

export function toTenantSettings(): NavTarget {
  return withParams(ROUTES.tenantSettings, {});
}

/** 開通直後の案内。 */
export function toStart(): NavTarget {
  return withParams(ROUTES.start, {});
}

/** MCP接続 (個人メニューの画面)。トークンの発行もここ。 */
export function toMcpSettings(): NavTarget {
  return withParams(ROUTES.mcpSettings, {});
}

/** 監査ログ。絞り込みは操作者名・操作・対象名に当たる。 */
export function toAuditLogs(query?: string): NavTarget {
  return withParams(ROUTES.auditLogs, { q: query });
}

const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * クエリ 1 つを読み書きする。
 *
 * 書き換えは `replace` で行う。月送りや 1 文字ごとの絞り込みで履歴が
 * 積み上がると、戻るボタンが画面を出るまで何度も押す羽目になる。
 */
function useParam(key: string): [string | null, (value: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const set = useCallback(
    (value: string | null) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value === null || value === '') next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: true },
      );
    },
    [key, setParams],
  );
  return [params.get(key), set];
}

/**
 * 対象月。URL に無ければ当月。
 * 読むだけでは URL を書き換えないので、ただ開いた画面の URL は素のまま。
 */
export function useMonthParam(): [string, (month: string) => void] {
  const [raw, setRaw] = useParam('month');
  const month = raw !== null && MONTH_PATTERN.test(raw) ? raw : currentMonth();
  return [month, setRaw];
}

/** 一覧の絞り込み。空文字ならクエリごと消える。 */
export function useQueryParam(): [string, (value: string) => void] {
  const [raw, setRaw] = useParam('q');
  return [raw ?? '', setRaw];
}

/** 一覧で開いている行。プロジェクトは `project`、メンバーは `user`。 */
export function useSelectedParam(
  key: 'project' | 'user',
): [string | null, (id: string | null) => void] {
  return useParam(key);
}
